import { useState } from 'react';
import { Box, Divider, Stack, Typography } from '@mui/material';
import { CompanyAdminDto } from 'tdc-web-backend/companies/schemas';
import { FormProvider, useForm } from 'react-hook-form';
import { CompanyRole, EnumContractType, mapContractType } from 'tdc-web-backend/enums/enums';
import { AxiosError } from 'axios';
import { useNavigate } from 'react-router-dom';
import ControlledTextInput from '../../../../../components/custom-inputs/ControlledTextInput';
import useGetMany from '../../../../../utils/hooks/crud-hooks/useGetMany';
import BaseInputLabel from '../../../../../components/custom-inputs/BaseInputLabel';
import LoadingButton from '../../../../../components/button/LoadingButton';
import useUpdate from '../../../../../utils/hooks/crud-hooks/useUpdate';
import ConfirmModal from '../../../../../components/confirm-modal/ConfirmModal';
import { secondaryPink } from '../../../../../utils/color';
import { ReactComponent as ErrorAlertIcon } from '../../../../../assets/icons/layout-icons/ErrorAlertIcon.svg';
import CustomInput from '../../../../../components/custom-input/CustomInput';
import CustomInputAutocomplete from '../../../../../components/custom-input/inputs/CustomInputAutocomplete';
import CustomInputSelect from '../../../../../components/custom-input/inputs/CustomInputSelect';

const CreateProject = ({ projectRequestData }: { projectRequestData: any }) => {
  const navigate = useNavigate();
  const [isErrModalOpen, setIsErrModalOpen] = useState(false);
  const [errModalMsg, setErrModalMsg] = useState('');

  const requestedBy = projectRequestData?.company;
  const isBuyer = requestedBy?.roles.includes(CompanyRole.Buyer);

  const formMethods = useForm({
    defaultValues: {
      name: projectRequestData.projectName,
      description: projectRequestData.projectDescription,
      buyer: '',
      seller: '',
      contractType: EnumContractType.Default,
    },
  });
  const requestedByContractType = mapContractType.get(
    (requestedBy as unknown as CompanyAdminDto).defaultContractType,
  );

  const { data: sellerData } = useGetMany<CompanyAdminDto>({
    resource: '/companies/admin?role=seller_tool&role=seller_service',
    enabled: !!projectRequestData && isBuyer,
  });
  const { data: buyerData } = useGetMany<CompanyAdminDto>({
    resource: '/companies/admin?role=buyer',
    enabled: !!projectRequestData && !isBuyer,
  });

  let participant = null;
  if (isBuyer) {
    participant = formMethods.watch('seller');
  } else {
    participant = formMethods.watch('buyer');
  }
  const participantCompanyContractType =
    participant !== ''
      ? mapContractType.get((participant as unknown as CompanyAdminDto)?.defaultContractType)
      : '';
  const participantCompanyId =
    participant !== '' ? (participant as unknown as CompanyAdminDto)?.id : '';

  const { mutate, isLoading } = useUpdate({ resource: `project-requests` });

  const { handleSubmit } = formMethods;

  const submitForm = (data: any) => {
    const submitData = {
      participantCompany: participantCompanyId,
      contractType: formMethods.getValues('contractType'),
      name: formMethods.getValues('name'),
      description: formMethods.getValues('description'),
    };

    mutate(
      {
        id: projectRequestData.id,
        data: submitData,
        suffix: 'create-project',
      },
      {
        onSuccess: (data) => {
          navigate(`../projects/${(data?.data as any).id as string}`);
        },
        onError: (err) => {
          setErrModalMsg((err as AxiosError).response?.data.message || 'Something went wrong');
          setIsErrModalOpen(true);
        },
      },
    );
  };

  return (
    <>
      <FormProvider {...formMethods}>
        <form onSubmit={handleSubmit(submitForm)}>
          <Stack spacing={1} sx={{ mt: '3rem' }}>
            <Stack spacing={3}>
              <Stack spacing={1}>
                <Box sx={{ mb: 0 }}>
                  <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                    Title
                  </Typography>
                  <Typography variant="caption">
                    The title should include the necessery keywords and be between 4-9 words long.
                  </Typography>
                </Box>
                <ControlledTextInput name="name" placeholder="Title" required />
              </Stack>
              <Stack spacing={1}>
                <Box sx={{ mb: 0 }}>
                  <BaseInputLabel text="Buyer:" sx={{ fontSize: '14px !important', mb: 0 }} />
                </Box>

                {!isBuyer && (
                  <CustomInput
                    input={<CustomInputAutocomplete />}
                    name="buyer"
                    choices={buyerData?.data.results}
                    labelKey="name"
                    valueKey="id"
                    keepOnlyValueKeys={false}
                    placeholder="Select"
                    required
                    fullWidth
                    sx={{ minHeight: 'none', width: '300px', mt: 2 }}
                    disableClearable
                  />
                )}

                {isBuyer && (
                  <Typography variant="body1">{projectRequestData.company.name}</Typography>
                )}
              </Stack>
              <Stack spacing={1}>
                <Box sx={{ mb: 0 }}>
                  <BaseInputLabel text="Seller:" sx={{ fontSize: '14px !important', mb: 0 }} />
                </Box>

                {isBuyer && (
                  <CustomInput
                    input={<CustomInputAutocomplete />}
                    name="seller"
                    choices={sellerData?.data.results}
                    labelKey="name"
                    valueKey="id"
                    keepOnlyValueKeys={false}
                    placeholder="Select"
                    required
                    fullWidth
                    sx={{ minHeight: 'none', mt: 2 }}
                    disableClearable
                  />
                )}

                {!isBuyer && (
                  <Typography variant="body1">{projectRequestData.company.name}</Typography>
                )}
              </Stack>
              <Stack spacing={1}>
                <Box sx={{ mb: 0 }}>
                  <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                    Description
                  </Typography>
                </Box>
                <ControlledTextInput name="description" multiline minRows={4} required />
              </Stack>
            </Stack>
            <Divider sx={{ pt: 3 }} />
            <Stack spacing={1} sx={{ pt: 3 }}>
              <Box sx={{ pb: 2 }}>
                <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                  Contract type
                </Typography>
              </Box>
              <Stack spacing={3}>
                <CustomInput
                  input={<CustomInputSelect />}
                  name="contractType"
                  choices={mapContractType}
                  label="Project contract type"
                  placeholder="Select the project contract type"
                  rules={{ required: 'Contract type is required' }}
                  sx={{ minHeight: 'none', maxWidth: '600px', mt: 2 }}
                />
                <Stack spacing={1}>
                  <Stack direction="row" spacing={2}>
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                      * Buyer defautl contract type:
                    </Typography>
                    <Typography variant="body2" sx={{}}>
                      {isBuyer ? requestedByContractType : participantCompanyContractType}
                    </Typography>
                  </Stack>
                  <Stack direction="row" spacing={2}>
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                      * Seller defautl contract type:
                    </Typography>
                    <Typography variant="body2" sx={{}}>
                      {isBuyer ? participantCompanyContractType : requestedByContractType}
                    </Typography>
                  </Stack>
                </Stack>
              </Stack>
            </Stack>

            <Divider sx={{ pt: 5 }} />

            <LoadingButton
              onButtonClick={() => null}
              sx={{ alignSelf: 'end' }}
              variant="contained"
              size="medium"
              fontWeight="bold"
              minWidth={102}
              type="submit"
              loading={isLoading}
            >
              Create project
            </LoadingButton>
          </Stack>
        </form>
      </FormProvider>
      <ConfirmModal
        isModalOpen={isErrModalOpen}
        setIsModalOpen={setIsErrModalOpen}
        title="Error"
        message={
          <Typography color="primaryDark.500" lineHeight="21px">
            {errModalMsg}
          </Typography>
        }
        confirmButtonText="Close"
        onConfirm={() => navigate('../project-requests')}
        iconProps={{
          icon: <ErrorAlertIcon fill={secondaryPink[700]} style={{ width: 24, height: 24 }} />,
          wrapperBackgroundColor: secondaryPink[100],
        }}
        showCancelButton={false}
      />
    </>
  );
};

export default CreateProject;
